import React from 'react'
import PropTypes from 'prop-types'

/**
 * FormButtons component renders action buttons for form submission, update, and deletion.
 * It displays different sets of buttons based on whether the form is in add, update, or view-only mode.
 *
 * @module form/FormButtons
 * @description FormButtons component renders action buttons for form submission, update, and deletion.
 * It displays different sets of buttons based on whether the form is in add, update, or view-only mode.
 * @param {boolean} [isViewOnly=false] - If true, the buttons are hidden and the form is view-only.
 * @param {Object} defaultData - The default data for the form, used primarily for identifying records during updates or deletions.
 * @param {function} confirmDelete - Function to call when the delete button is clicked.
 * @param {boolean} [isAdd=false] - If true, the form is in "add" mode, displaying add-related buttons.
 * @param {boolean} [isRowForm=false] - If true, the buttons are styled for a row-form layout.
 * @param {function} setOpenFormModal - Function to toggle the visibility of the form modal.
 * @param {boolean} isUpdate - If true, indicates the form is in update mode, showing update-related buttons.
 * @param {Object} createCallMutation - Mutation object handling the add form submission.
 * @param {Object} updateCallMutation - Mutation object handling the update form submission.
 * @param {Object} deleteCallMutation - Mutation object handling the delete action.
 * @param {Object} formSchema - Schema object defining the form structure, used to retrieve field identifiers.
 * @param {int} crud - Crud level of the module
 *
 * @example
 * <FormButtons
 *   isViewOnly={isViewOnly}
 *   isAdd={isAdd}
 *   isRowForm={isRowForm}
 *   createCallMutation={createCallMutation}
 *   updateCallMutation={updateCallMutation}
 *   deleteCallMutation={deleteCallMutation}
 *   confirmDelete={confirmDelete}
 *   isUpdate={isUpdate}
 *   defaultData={defaultData}
 *   setOpenFormModal={setOpenFormModal}
 *   formSchema={formSchema}
 *   crud={crud}
 * />
 *
 * @returns {React.Element} The rendered FormButtons component.
 */
export const FormButtons = ({
  isViewOnly = false,
  isAdd = false,
  isRowForm = false,
  noClose,
  createCallMutation,
  updateCallMutation,
  deleteCallMutation,
  confirmDelete,
  isUpdate,
  defaultData,
  setOpenFormModal,
  formSchema,
  crud,
}) => {
  if (isViewOnly) return null

  return (
    <div className="trtui-col-12">
      {isAdd ? (
        <>
          <button
            className={`trtui-btn trtui-btn-primary ${
              isRowForm ? 'trtui-btn-icon trtui-btn-sm' : ''
            }`}
            type="submit"
            disabled={createCallMutation.isLoading}
          >
            {createCallMutation.isLoading ? (
              <span className="trtui-d-flex trtui-align-items-center">
                <span className="trtui-flex-grow-1 trtui-me-2">
                  {isRowForm ? '...' : 'Adding...'}
                </span>
                <span className="trtui-spinner-border trtui-flex-shrink-0" role="status">
                  <span className="trtui-visually-hidden">{isRowForm ? '...' : 'Adding...'}</span>
                </span>
              </span>
            ) : isRowForm ? (
              <i className="ri-delete-bin-5-line" />
            ) : (
              'Add'
            )}
          </button>{' '}
          <button
            className={`trtui-btn trtui-btn-light ${
              isRowForm ? 'trtui-btn-icon trtui-btn-sm' : ''
            }`}
            onClick={() => setOpenFormModal(false)}
            type="button"
            disabled={createCallMutation.isLoading}
          >
            {isRowForm ? <i className="ri-close-line" /> : 'Close'}
          </button>
        </>
      ) : (
        <>
          <button
            className="trtui-btn trtui-btn-success"
            type="submit"
            disabled={updateCallMutation.isLoading || deleteCallMutation.isLoading}
          >
            {updateCallMutation.isLoading ? (
              <span className="trtui-d-flex trtui-align-items-center">
                <span className="trtui-flex-grow-1 trtui-me-2">
                  {isRowForm ? '...' : 'Updating...'}
                </span>
                <span className="trtui-spinner-border trtui-flex-shrink-0" role="status">
                  <span className="trtui-visually-hidden">{isRowForm ? '...' : 'Updating...'}</span>
                </span>
              </span>
            ) : isRowForm ? (
              <i className="ri-check-line" />
            ) : (
              'Update'
            )}
          </button>{' '}
          {isUpdate && crud > 3 ? (
            <button
              className="trtui-btn trtui-btn-danger"
              onClick={() => confirmDelete(defaultData[formSchema.id])}
              type="button"
              disabled={updateCallMutation.isLoading || deleteCallMutation.isLoading}
            >
              {deleteCallMutation.isLoading ? (
                <span className="trtui-d-flex trtui-align-items-center">
                  <span className="trtui-flex-grow-1 trtui-me-2">
                    {isRowForm ? '...' : 'Deleting...'}
                  </span>
                  <span className="trtui-spinner-border trtui-flex-shrink-0" role="status">
                    <span className="trtui-visually-hidden">
                      {isRowForm ? '...' : 'Deleting...'}
                    </span>
                  </span>
                </span>
              ) : isRowForm ? (
                <i className="ri-delete-bin-line" />
              ) : (
                'Delete'
              )}
            </button>
          ) : (
            !noClose && (
              <button
                className="trtui-btn trtui-btn-light"
                onClick={() => setOpenFormModal(false)}
                type="button"
                disabled={
                  updateCallMutation.isLoading ||
                  createCallMutation.isLoading ||
                  updateCallMutation.isLoading ||
                  deleteCallMutation.isLoading
                }
              >
                Close
              </button>
            )
          )}
        </>
      )}
    </div>
  )
}

FormButtons.propTypes = {
  /**
   * If true, the buttons are hidden and the form is view-only.
   */
  isViewOnly: PropTypes.bool,

  /**
   * The default data for the form, used primarily for identifying records during updates or deletions.
   */
  defaultData: PropTypes.object,

  /**
   * Function to call when the delete button is clicked.
   */
  confirmDelete: PropTypes.func,

  /**
   * If true, the form is in "add" mode, displaying add-related buttons.
   */
  isAdd: PropTypes.bool,

  /**
   * If true, the buttons are styled for a row-form layout.
   */
  isRowForm: PropTypes.bool,

  /**
   * Function to toggle the visibility of the form modal.
   */
  setOpenFormModal: PropTypes.func,

  /**
   * If true, indicates the form is in update mode, showing update-related buttons.
   */
  isUpdate: PropTypes.bool,

  /**
   * Mutation object handling the add form submission.
   */
  createCallMutation: PropTypes.object,

  /**
   * Mutation object handling the update form submission.
   */
  updateCallMutation: PropTypes.object,

  /**
   * Mutation object handling the delete action.
   */
  deleteCallMutation: PropTypes.object,

  /**
   * Schema object defining the form structure, used to retrieve field identifiers.
   */
  formSchema: PropTypes.object,
  crud: PropTypes.number,
  noClose: PropTypes.bool,
}
